/**
 * obtain (수주) orval 래퍼
 */
import { customInstance } from "../custom-instance";
import { DefaultApprovalStatusSchema, TempApprovalStatusSchema, } from "./default-schemes";
import { createUrlWithQueryByInputItem } from "@erp/shared/utils/https/create-url-with-query";
import { trimObjectArray } from "@erp/shared/utils/format/trim";
import { CalcCostDetailDataSchema } from "@erp/shared/components/ui/Document/CalcCost/const";
import { LaborCostEntrySchema } from "@erp/shared/components/ui/Dialog/LaborCostDialog/labor-cost-entry.types";
import { validateArrayWithSchema, validateWithSchema, } from "@erp/shared/utils/https/zod-validation";
import { z } from "zod";
import { GetProductsResponseSchema, } from "./product";
import { BOMInfoSchema } from "./estimate";
// ── 스키마 & 타입 (ObtainConfirm) ──
export const GetObtainConfirmResponseSchema = z
    .object({
    ...DefaultApprovalStatusSchema.shape,
    ...TempApprovalStatusSchema.shape,
    id: z.coerce.number(),
    writeType: z.coerce.number().int().min(0).max(1),
    createrPhoneNumber: z.string().nullable(),
    inhouseBidNumber: z.string().nullable(),
    companyBidNumber: z.string().nullable(),
    totalPrice: z.number(),
    companyId: z.number().nullable(),
    companyName: z.string().nullable(),
    managerId: z.number().nullable(),
    managerName: z.string().nullable(),
    managerEmail: z.string().nullable(),
    managerPhone: z.string().nullable(),
    projectId: z.number().nullable(),
    projectCode: z.string().nullable(),
    obtainCalcHistoryId: z.coerce.number(),
    obtainLaborHistoryId: z.coerce.number().nullable(),
    obtainBomHistoryId: z.coerce.number(),
    projectIsPreOrder: z.number().nullable(),
    dueDate: z.string().nullable(),
    projectName: z.string().nullable(),
    projectPeriod: z.string().nullable(),
    deliveryCondition: z.string().nullable(),
    remark: z.string().nullable(),
    approvalFile: z.string().nullable(),
    blueprintFile: z.string().nullable(),
    obtainFile: z.string().nullable(),
    etcFiles: z.array(z.string()).nullable(),
    version: z.coerce.number(),
})
    .strict();
// ── 스키마 & 타입 (ObtainConfirmDetail) ──
export const ObtainDetailInfoSchema = z
    .object({
    ...GetObtainConfirmResponseSchema.shape,
})
    .strict();
export const GetObtainConfirmDetailResponseSchema = z
    .object({
    info: ObtainDetailInfoSchema,
    calc: z.array(CalcCostDetailDataSchema),
    labor: z.array(LaborCostEntrySchema),
    bom: BOMInfoSchema.nullable(),
})
    .strict();
// ── 스키마 & 타입 (HistoryVersions) ──
export const GetObtainHistoryVersionsResponseSchema = ObtainDetailInfoSchema.extend({
    historyType: z.string(),
    historyReason: z.string().nullable(),
    originId: z.coerce.number(),
}).strict();
// ── 스키마 & 타입 (HistoryVersionData) ──
export const GetObtainHistoryVersionDataResponseSchema = GetObtainConfirmDetailResponseSchema.extend({
    info: ObtainDetailInfoSchema.extend({
        originId: z.coerce.number(),
        historyType: z.string(),
        historyReason: z.string().nullable(),
    }),
}).strict();
export const GetObtainBOMWithStockResponseSchema = z
    .object({
    ...GetProductsResponseSchema.shape,
    quantity: z.number(),
    unitPrice: z.number().optional(),
    children: z.array(z.lazy(() => GetObtainBOMWithStockResponseSchema)),
    stockNum: z.number(),
})
    .strict();
// ── API 함수 ──
export async function getObtainConfirm(searchCardInputs) {
    const inputs = trimObjectArray(searchCardInputs);
    const url = createUrlWithQueryByInputItem("/obtain", inputs);
    const result = await customInstance({
        url,
        method: "GET",
    });
    return validateArrayWithSchema(GetObtainConfirmResponseSchema, result);
}
export async function searchObtainWithReject(searchCardInputs) {
    const inputs = trimObjectArray(searchCardInputs);
    const url = createUrlWithQueryByInputItem("/obtain?withRejected=true", inputs);
    const result = await customInstance({
        url,
        method: "GET",
    });
    return validateArrayWithSchema(GetObtainConfirmResponseSchema, result);
}
export async function getObtainConfirmDetail(id) {
    const res = await customInstance({
        url: "/obtain/" + id,
        method: "GET",
    });
    return validateWithSchema(GetObtainConfirmDetailResponseSchema, res);
}
export const postObtainConfirm = async (body) => {
    return customInstance({
        url: "/obtain",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: body,
    });
};
export const patchObtainConfirm = async (id, body) => {
    return customInstance({
        url: `/obtain/${id}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: body,
    });
};
export async function patchObtainConfirmApprove(obtainConfirmId, approvalPhase, rejectReason, deleteProject) {
    return customInstance({
        url: `/obtain/approve/${obtainConfirmId}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: {
            approvalPhase,
            rejectReason: rejectReason || null,
            deleteProject: deleteProject || false,
        },
    });
}
export async function checkObtainDuplicate(inhouseBidNumber, excludeId) {
    const params = { inhouseBidNumber };
    if (excludeId != null) {
        params.excludeId = excludeId;
    }
    const response = await customInstance({
        url: "/obtain/check-duplicate",
        method: "GET",
        params,
    });
    return response.exists;
}
export async function getObtainHistoryVersions(originId) {
    const data = await customInstance({
        url: `/obtain/${originId}/history`,
        method: "GET",
    });
    if (!data) {
        throw new Error("Failed to fetch data for the selected version.");
    }
    return validateArrayWithSchema(GetObtainHistoryVersionsResponseSchema, data);
}
export async function getObtainHistoryVersionData(historyId) {
    const data = await customInstance({
        url: `/obtain/history/${historyId}`,
        method: "GET",
    });
    return validateWithSchema(GetObtainHistoryVersionDataResponseSchema, data);
}
export const getObtainBOM = async (id) => {
    const data = await customInstance({
        url: `/obtain/bom-stock/${id}`,
        method: "GET",
    });
    return validateArrayWithSchema(GetObtainBOMWithStockResponseSchema, data);
};
// ── SDK 객체 ──
export const obtainApi = {
    getObtainConfirm,
    searchObtainWithReject,
    getObtainConfirmDetail,
    postObtainConfirm,
    patchObtainConfirm,
    patchObtainConfirmApprove,
    checkObtainDuplicate,
    getObtainHistoryVersions,
    getObtainHistoryVersionData,
    getObtainBOM,
};
